import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Container, Row, Col, Spinner, Button } from "react-bootstrap";
import { Title } from "../components/common/Title";
import { useMyPlayer } from "../context/MyPlayerContext";
import EpicLeaderboardFilters from "../components/epicLeaderboard/EpicLeaderboardFilters";
import EpicFirstRecordCard from "../components/epicLeaderboard/EpicFirstRecordCard";
import EpicStatTiles from "../components/epicLeaderboard/EpicStatTiles";
import EpicTrendChart from "../components/epicLeaderboard/EpicTrendChart";
import EpicDistributionChart from "../components/epicLeaderboard/EpicDistributionChart";
import EpicBossChart from "../components/epicLeaderboard/EpicBossChart";
import EpicLeaderboardTable from "../components/epicLeaderboard/EpicLeaderboardTable";
import EpicLeaderboardTableSkeleton from "../components/epicLeaderboard/EpicLeaderboardTableSkeleton";
import "../components/epicLeaderboard/epicLeaderboard.css";
import {
  DEFAULT_EPIC_SLUG,
  DEFAULT_RANGE,
  DEFAULT_SORT,
  EPIC_SLUGS,
  RANGE_OPTIONS,
  SORT_OPTIONS,
  defaultMinRunsFor
} from "../constants/epicLeaderboard";
import type { RangeOption } from "../constants/epicLeaderboard";
import type {
  EpicLeaderboardResponse,
  EpicLeaderboardRow,
  EpicLeaderboardSort,
  EpicLeaderboardSummary
} from "../types/epicLeaderboard";
import {
  useComponentTracking,
  useTimeTracking,
  analytics
} from "../utils/analytics";
import { rangeToStartDate } from "../utils/rangeToStartDate";

const PLAYER_DEBOUNCE_MS = 300;
const PAGE_LIMIT = 25;

const parseEpicSlug = (value: string | null): string =>
  value && Object.prototype.hasOwnProperty.call(EPIC_SLUGS, value)
    ? value
    : DEFAULT_EPIC_SLUG;

const parseRange = (value: string | null): RangeOption =>
  RANGE_OPTIONS.find((o) => o.value === value)?.value ?? DEFAULT_RANGE;

const parseSort = (value: string | null): EpicLeaderboardSort =>
  SORT_OPTIONS.find((o) => o.value === value)?.value ?? DEFAULT_SORT;

const parseIntParam = (
  value: string | null,
  min: number,
  max: number
): number | undefined => {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const n = Number(value);
  return n >= min && n <= max ? n : undefined;
};

type ParamChanges = Partial<
  Record<"epic" | "range" | "sort" | "minRuns" | "player" | "page", string>
>;

const PARAM_DEFAULTS: Record<string, string> = {
  epic: DEFAULT_EPIC_SLUG,
  range: DEFAULT_RANGE,
  sort: DEFAULT_SORT,
  page: "1"
};

const ErrorBox = ({
  message,
  onRetry
}: {
  message: string;
  onRetry: () => void;
}) => (
  <div className="epic-lb-card text-center my-3">
    <div className="mb-2">{message}</div>
    <Button size="sm" variant="outline-light" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

export const EpicLeaderboard = () => {
  useComponentTracking("EpicLeaderboard");
  useTimeTracking("EpicLeaderboard");

  const [searchParams, setSearchParams] = useSearchParams();
  const { myPlayer } = useMyPlayer();

  const epicSlug = parseEpicSlug(searchParams.get("epic"));
  const epicName = EPIC_SLUGS[epicSlug];
  const range = parseRange(searchParams.get("range"));
  const sort = parseSort(searchParams.get("sort"));
  const minRunsParam = parseIntParam(searchParams.get("minRuns"), 1, 100);
  const effectiveMinRuns = minRunsParam ?? defaultMinRunsFor(sort);
  const urlPlayer = (searchParams.get("player") ?? "").trim();
  const page = parseIntParam(searchParams.get("page"), 1, 100000) ?? 1;

  // Writes go through the functional form so the debounced player write
  // always merges into the latest URL rather than a stale closure's copy.
  const updateParams = useCallback(
    (
      changes: ParamChanges,
      { resetPage = true, replace = false } = {}
    ) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([key, value]) => {
            if (value === undefined || value === "" || value === PARAM_DEFAULTS[key]) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          if (resetPage && !("page" in changes)) next.delete("page");
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  // Player search: the input is local state so typing stays instant; the URL
  // (and therefore the fetch) only follows after a pause, with replace so
  // back/forward isn't flooded with one entry per keystroke.
  const [playerInput, setPlayerInput] = useState(urlPlayer);
  const lastWrittenPlayer = useRef(urlPlayer);

  useEffect(() => {
    // URL changed from outside (back/forward, hotlink) - mirror it into the
    // input. Skipped for values this component wrote itself.
    if (urlPlayer !== lastWrittenPlayer.current) {
      lastWrittenPlayer.current = urlPlayer;
      setPlayerInput(urlPlayer);
    }
  }, [urlPlayer]);

  useEffect(() => {
    const trimmed = playerInput.trim();
    if (trimmed === lastWrittenPlayer.current) return undefined;
    const timer = setTimeout(() => {
      lastWrittenPlayer.current = trimmed;
      updateParams({ player: trimmed }, { replace: true });
      if (trimmed) analytics.trackSearch(trimmed, "epic_leaderboard_player");
    }, PLAYER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [playerInput, updateParams]);

  // Summary: only depends on epic + range, never page/sort.
  const [summary, setSummary] = useState<EpicLeaderboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);
  const [summaryReload, setSummaryReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setSummaryLoading(true);
    setSummaryError(false);
    setSummary(null);

    const params: Record<string, string> = { epic: epicName };
    const startDate = rangeToStartDate(range);
    if (startDate) params.startDate = startDate;

    axios
      .get<EpicLeaderboardSummary>(
        `${process.env.REACT_APP_API}public/epic-leaderboard/summary`,
        { params, signal: controller.signal }
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        setSummary(res.data);
        setSummaryLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        console.error("Failed to fetch epic leaderboard summary", err);
        analytics.trackError("epic_leaderboard_summary", String(err?.message));
        setSummaryError(true);
        setSummaryLoading(false);
      });

    return () => controller.abort();
  }, [epicName, range, summaryReload]);

  // Leaderboard table: depends on every filter. Previous rows stay on screen
  // (dimmed, with an overlay spinner) while a refetch is in flight.
  const [leaderboard, setLeaderboard] =
    useState<EpicLeaderboardResponse | null>(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState(false);
  const [tableReload, setTableReload] = useState(0);
  const loadedEpicRef = useRef(epicName);

  useEffect(() => {
    const controller = new AbortController();
    setTableLoading(true);
    setTableError(false);
    // Rows from another epic would be misleading even when dimmed.
    if (loadedEpicRef.current !== epicName) {
      loadedEpicRef.current = epicName;
      setLeaderboard(null);
    }

    const params: Record<string, string | number> = {
      epic: epicName,
      sort,
      page,
      limit: PAGE_LIMIT
    };
    const startDate = rangeToStartDate(range);
    if (startDate) params.startDate = startDate;
    if (minRunsParam !== undefined) params.minRuns = minRunsParam;
    if (urlPlayer) params.player = urlPlayer;

    axios
      .get<EpicLeaderboardResponse>(
        `${process.env.REACT_APP_API}public/epic-leaderboard`,
        { params, signal: controller.signal }
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        // A hotlink or stale back-nav can point past the last page; land on
        // the last real page instead of showing an empty table.
        const { totalPages } = res.data.pagination;
        if (res.data.rows.length === 0 && totalPages > 0 && page > totalPages) {
          updateParams(
            { page: String(totalPages) },
            { resetPage: false, replace: true }
          );
          return;
        }
        setLeaderboard(res.data);
        setTableLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        console.error("Failed to fetch epic leaderboard", err);
        analytics.trackError("epic_leaderboard", String(err?.message));
        setTableError(true);
        setTableLoading(false);
      });

    return () => controller.abort();
  }, [epicName, range, sort, minRunsParam, urlPlayer, page, tableReload, updateParams]);

  // The viewer's own place on this board, fetched on its own so it shows even
  // when the table is filtered or paged elsewhere. undefined = nothing to show
  // yet; null = they are not on this board.
  const [mine, setMine] = useState<EpicLeaderboardRow | null | undefined>(
    undefined
  );
  // Total ranked players, known from any unfiltered table response.
  const boardKey = `${epicName}|${range}|${sort}|${minRunsParam ?? ""}`;
  const [rankedTotal, setRankedTotal] = useState<{
    key: string;
    total: number;
  } | null>(null);

  useEffect(() => {
    if (!leaderboard || urlPlayer) return;
    setRankedTotal({ key: boardKey, total: leaderboard.pagination.totalCount });
  }, [leaderboard, urlPlayer, boardKey]);

  useEffect(() => {
    if (!myPlayer) {
      setMine(undefined);
      return undefined;
    }
    const controller = new AbortController();
    const params: Record<string, string | number> = {
      epic: epicName,
      sort,
      page: 1,
      limit: 10,
      player: myPlayer
    };
    const startDate = rangeToStartDate(range);
    if (startDate) params.startDate = startDate;
    if (minRunsParam !== undefined) params.minRuns = minRunsParam;

    axios
      .get<EpicLeaderboardResponse>(
        `${process.env.REACT_APP_API}public/epic-leaderboard`,
        { params, signal: controller.signal }
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        // The filter is a name substring, so pick the exact match.
        setMine(
          res.data.rows.find(
            (row) => row.player.toLowerCase() === myPlayer.toLowerCase()
          ) ?? null
        );
      })
      .catch(() => {
        if (!controller.signal.aborted) setMine(undefined);
      });
    return () => controller.abort();
  }, [myPlayer, epicName, range, sort, minRunsParam]);

  const handleEpicChange = (slug: string) => {
    analytics.trackFeatureUsage("EpicLeaderboard", "epic_select", { epic: slug });
    updateParams({ epic: slug });
  };

  const stats = summary?.stats;
  const showTrend = (summary?.trend.length ?? 0) >= 2;
  const showDistribution = (summary?.distribution.length ?? 0) >= 2;
  const showBosses = (summary?.bossFrequency.length ?? 0) > 0;
  const chartCols = showTrend && showDistribution ? 6 : 12;

  return (
    <Container className="py-4">
      <Title text="Epic Leaderboard" />
      <p className="text-light">
        Clear times for epics run by small groups (fewer than 6 players).
        Everyone in a run shares its time.
      </p>

      <EpicLeaderboardFilters
        epicSlug={epicSlug}
        range={range}
        sort={sort}
        minRuns={effectiveMinRuns}
        playerInput={playerInput}
        onEpicChange={handleEpicChange}
        onRangeChange={(value) => updateParams({ range: value })}
        onSortChange={(value) => updateParams({ sort: value })}
        onMinRunsChange={(value) =>
          updateParams({
            minRuns:
              value === defaultMinRunsFor(sort) ? undefined : String(value)
          })
        }
        onPlayerInputChange={setPlayerInput}
      />

      <div className="mb-3">
        <EpicFirstRecordCard epicName={epicName} />
      </div>

      {summaryLoading && (
        <div className="text-center my-4">
          <Spinner animation="border" variant="light" />
        </div>
      )}
      {summaryError && (
        <ErrorBox
          message="Couldn't load stats for this epic."
          onRetry={() => setSummaryReload((n) => n + 1)}
        />
      )}
      {summary && stats && !summaryLoading && !summaryError && (
        <>
          {stats.totalRuns > 0 && (
            <>
              <EpicStatTiles stats={stats} />
              {(showTrend || showDistribution || showBosses) && (
                <Row className="g-3 mb-4">
                  {showTrend && (
                    <Col xs={12} lg={chartCols}>
                      <div className="epic-lb-card">
                        <div className="epic-lb-card-title">
                          Clear time trend
                        </div>
                        <EpicTrendChart
                          epicName={epicName}
                          trend={summary.trend}
                        />
                      </div>
                    </Col>
                  )}
                  {showDistribution && (
                    <Col xs={12} lg={chartCols}>
                      <div className="epic-lb-card">
                        <div className="epic-lb-card-title">
                          Time distribution
                        </div>
                        <EpicDistributionChart
                          epicName={epicName}
                          distribution={summary.distribution}
                        />
                      </div>
                    </Col>
                  )}
                  {showBosses && (
                    <Col xs={12}>
                      <div className="epic-lb-card">
                        <div className="epic-lb-card-title">Boss draws</div>
                        <div className="epic-lb-muted mb-2">
                          Share of runs each boss showed up in.
                        </div>
                        <EpicBossChart
                          epicName={epicName}
                          bosses={summary.bossFrequency}
                        />
                      </div>
                    </Col>
                  )}
                </Row>
              )}
            </>
          )}
        </>
      )}

      {myPlayer && mine !== undefined && (
        <div className="epic-lb-card mb-3 epic-lb-mine">
          {mine ? (
            <>
              <span className="epic-lb-mine-label">★ You: #{mine.rank.toLocaleString()}
                {rankedTotal?.key === boardKey ? ` of ${rankedTotal.total.toLocaleString()}` : ""}
              </span>
              <span className="epic-lb-muted">
                {" "}· Fastest {mine.fastest} · {mine.runCount.toLocaleString()} runs
              </span>
              {urlPlayer.toLowerCase() !== myPlayer.toLowerCase() && (
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 ms-2"
                  onClick={() => setPlayerInput(myPlayer)}
                >
                  Show my row
                </Button>
              )}
            </>
          ) : (
            <span className="epic-lb-muted">
              ★ You aren&apos;t on this board: no runs in this range, or fewer than {effectiveMinRuns} run{effectiveMinRuns === 1 ? "" : "s"}.
            </span>
          )}
        </div>
      )}

      {tableError ? (
        <ErrorBox
          message="Couldn't load the leaderboard."
          onRetry={() => setTableReload((n) => n + 1)}
        />
      ) : !leaderboard ? (
        <div className="epic-lb-table-wrap">
          <div className="epic-lb-refetching">
            <EpicLeaderboardTableSkeleton />
          </div>
          <div className="epic-lb-overlay">
            <Spinner animation="border" variant="light" />
          </div>
        </div>
      ) : leaderboard.rows.length === 0 ? (
        <div className="epic-lb-table-wrap">
          <div
            className={`epic-lb-card text-center${
              tableLoading ? " epic-lb-refetching" : ""
            }`}
          >
            {urlPlayer ? "No players match your search." : "No runs in this range."}
          </div>
          {tableLoading && (
            <div className="epic-lb-overlay">
              <Spinner animation="border" variant="light" />
            </div>
          )}
        </div>
      ) : (
        <EpicLeaderboardTable
          rows={leaderboard.rows}
          pagination={leaderboard.pagination}
          sort={sort}
          refetching={tableLoading}
          onPageChange={(value) => updateParams({ page: String(value) }, { resetPage: false })}
        />
      )}
    </Container>
  );
};

export default EpicLeaderboard;
